import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { Button } from 'components/Form/Button/Button';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const EXPANDED_PADDING = 48; // px-6 = 24px each side
const COLLAPSED_PADDING = 16; // px-2 = 8px each side

type PDFViewerPanelProps = {
  onClose?: () => void;
  onExpand?: () => void;
  isCollapsed?: boolean;
  showDownload?: boolean;
  showClose?: boolean;
  collapsedLabel?: string;
  pdfUrl: string;
  filename: string;
};

export const PDFViewerPanel = ({
  onClose,
  onExpand,
  isCollapsed,
  showDownload = true,
  showClose = true,
  collapsedLabel,
  pdfUrl,
  filename,
}: PDFViewerPanelProps) => {
  const { t } = useTranslation();
  const hasHeader = !isCollapsed || showDownload || showClose;
  const [numPages, setNumPages] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setContainerWidth(width);
        }, 150);
      }
    });

    observer.observe(container);
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  const padding = isCollapsed ? COLLAPSED_PADDING : EXPANDED_PADDING;
  const pageWidth = containerWidth ? containerWidth - padding : undefined;

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {hasHeader && (
        <div className={`flex h-14 shrink-0 items-center ${isCollapsed ? 'justify-end px-2' : 'justify-between px-6'}`}>
          {!isCollapsed && <h3 className="truncate text-base font-medium leading-tight text-gray-900">{filename}</h3>}
          <div className="flex shrink-0 items-center gap-2">
            {showDownload && (
              <Button
                type="button"
                variant="secondary"
                size="extra-small"
                iconOnly={isCollapsed}
                LeftIcon={isCollapsed ? ArrowDownTrayIcon : undefined}
                RightIcon={isCollapsed ? undefined : ArrowDownTrayIcon}
                analyticsId="transactions:download_original_pdf"
                onClick={() => window.open(pdfUrl, '_blank')}
              >
                {isCollapsed ? null : t('common:download')}
              </Button>
            )}
            {showClose && (
              <Button
                type="button"
                variant="secondary"
                size="extra-small"
                iconOnly
                LeftIcon={XMarkIcon}
                analyticsId="transactions:close_pdf_viewer"
                onClick={onClose}
                aria-label={t('common:close')}
              />
            )}
          </div>
        </div>
      )}

      <div ref={containerRef} className="min-h-0 flex-1 overflow-y-auto">
        <Document file={pdfUrl} loading={null} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
          <div className={`flex flex-col items-center ${isCollapsed ? 'gap-2 p-2' : 'gap-4 px-6 pb-4'}`}>
            {Array.from({ length: numPages }, (_, i) => (
              <Page
                key={i + 1}
                pageNumber={i + 1}
                width={pageWidth}
                className={`shadow-lg ${isCollapsed ? 'cursor-pointer overflow-hidden rounded-md ring-2 ring-transparent transition-all hover:ring-gray-400 [&_.annotationLayer]:pointer-events-none [&_.textLayer]:pointer-events-none' : ''}`}
                onClick={isCollapsed ? onExpand : undefined}
              />
            ))}
            {isCollapsed && collapsedLabel && (
              <span className="text-xs font-medium text-gray-500">{collapsedLabel}</span>
            )}
          </div>
        </Document>
      </div>
    </div>
  );
};
